import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  Table,
  TableHeader,
  TableHeaders,
  Text,
} from '../../ui';
import type { Suggestion } from '../../core/suggestions/getSuggestions';
import type { SuggestionPerformanceMetrics } from '../../core/Core.voyage-metrics';
import type { ModalProps } from '../../ui/Modal/Modal';
import {
  getCargoSummaries,
  formatTotalPortSeaDays,
  getSuggestionTitle,
  getVoyageEndDates,
  getVoyageStartDates,
  hasTcRateData,
} from './estimatorHelpers';
import { TextRow, ValueRow } from './RowComponents';

export type SuggestionWithMetrics = Suggestion &
  Partial<SuggestionPerformanceMetrics> & {
    id?: string | number;
    vesselName?: string | null;
    vesselId?: number | null;
    fromPort?: string;
    toPort?: string;
    totalTcCost?: number | null;
    suggestionIndex: number;
  };

export type SuggestionEstimatorProps = Partial<ModalProps> & {
  suggestions?: SuggestionWithMetrics[];
  onSuggestionAccept?: (vesselId: number, suggestionIndex: number) => void;
};

export default function SuggestionEstimator({
  suggestions,
  onClose,
  onSuggestionAccept,
}: SuggestionEstimatorProps) {
  const windowWidth = useWindowWidth();

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const titles = suggestions.map(getSuggestionTitle);

  const columnWidth = 200;
  const numberOfColumns = suggestions?.length + 1;
  const width = numberOfColumns * columnWidth;
  const maxWidth = windowWidth - 100;

  return (
    <Modal
      isOpen
      onClose={() => onClose?.()}
      width={`${width}px`}
      maxWidth={`${maxWidth}px`}
      height="800px"
    >
      <ModalBody data-testid="suggestion-estimator-modal">
        <Heading mb={4}>Suggestion comparison</Heading>
        <Table data-testid="suggestion-estimator-table">
          <thead>
            <TableHeaders style={{ height: '50px' }}>
              <TableHeader width={`${columnWidth}px`}></TableHeader>
              {titles.map((title, i) => {
                const suggestion = suggestions[i];
                const { vesselId } = suggestion;
                return (
                  <TableHeader
                    key={`header-${suggestion.suggestionIndex}`}
                    width={`${columnWidth}px`}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Text
                        fontWeight="bold"
                        mb={2}
                        data-testid={`suggestion-estimator-title-${i}`}
                      >
                        {title}
                      </Text>
                      {onSuggestionAccept && vesselId && (
                        <Button
                          data-testid={`suggestion-estimator-accept-${i}`}
                          onPress={() => {
                            if (vesselId) {
                              onSuggestionAccept?.(
                                vesselId,
                                suggestion.suggestionIndex,
                              );
                              onClose?.();
                            }
                          }}
                        >
                          Add to this scenario
                        </Button>
                      )}
                    </Box>
                  </TableHeader>
                );
              })}
            </TableHeaders>
          </thead>
          <tbody>
            {/* Voyage Details Section */}
            <TextRow
              label="Start Date"
              values={getVoyageStartDates(suggestions)}
            />
            <TextRow label="End Date" values={getVoyageEndDates(suggestions)} />
            <TextRow
              label="Total days / port days / sea days"
              values={formatTotalPortSeaDays(suggestions)}
            />
            <TextRow label="Cargo" values={getCargoSummaries(suggestions)} />

            {/* Financial Metrics Section */}
            <ValueRow
              label="Ballast Δ"
              valueKey="ballastDelta"
              suggestions={suggestions}
              suffix="nm"
              isCurrency={false}
            />
            <ValueRow
              label="Delay Δ"
              valueKey="delayDelta"
              suggestions={suggestions}
              suffix="hours"
              isCurrency={false}
            />
            <ValueRow
              label="Duration Δ"
              valueKey="durationDelta"
              suggestions={suggestions}
              suffix="hours"
              isCurrency={false}
            />
            <ValueRow
              label="Mass Δ"
              getValue={(s) =>
                Math.round(s.vesselPerformanceMetrics.delta.totalMass)
              }
              suggestions={suggestions}
              suffix="mts"
              isCurrency={false}
            />
            <ValueRow
              label="Distance Δ"
              valueKey="distanceDelta"
              suggestions={suggestions}
              suffix="nm"
              isCurrency={false}
            />
            <ValueRow
              label="Bunker cost Δ"
              suggestions={suggestions}
              valueKey="bunkerCostDelta"
            />
            <ValueRow
              label="Voyage port cost"
              suggestions={suggestions}
              valueKey="voyagePortCost"
            />
            <ValueRow
              label="Port cost Δ"
              suggestions={suggestions}
              valueKey="portCostDelta"
            />
            <ValueRow label="Cost" suggestions={suggestions} valueKey="cost" />
            {hasTcRateData(suggestions) && (
              <ValueRow
                label="Total TC cost"
                suggestions={suggestions}
                valueKey="totalTcCost"
              />
            )}
            <ValueRow
              label="Income"
              suggestions={suggestions}
              valueKey="income"
            />
            <ValueRow
              label="Voyage TCE"
              suggestions={suggestions}
              valueKey="tce"
              suffix="/day"
            />
          </tbody>
        </Table>
      </ModalBody>
    </Modal>
  );
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return width;
}
